import React, { useState, useEffect } from 'react';
import { TeamType } from 'app/gamma/src/modules/state/models/teams';
import { InviteTeamMembersForm } from 'app/scripts/views/organization/InviteTeamMembers/InviteTeamMembersForm';
import { TeamIllustrationAnimation } from 'app/scripts/views/organization/InviteTeamMembers/TeamIllustrationAnimation';
import { CloseButton } from 'app/src/components/CloseButton';
import { CloseIcon } from '@trello/nachos/icons/close';
import { OrganizationsResponse } from 'app/gamma/src/types/responses';
import { normalizeTeam } from 'app/gamma/src/api/normalizers/team';
import { CreateTeamPanel } from './create-team-panel';
import RouterLink from 'app/common/components/singlespa/router-link';
import { forTemplate } from 'app/common/lib/util/i18n';
import { ButtonLink } from 'app/common/components/controls/link/link';
import styles from './create-team.less';
import { TeamTestIds } from '@trello/test-ids';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { TrelloStorage } from '@trello/storage';
import { memberId } from '@trello/session-cookie';

const format = forTemplate('create_team');
const getEmailDomain = (s?: string | null): string => {
  const [, domain] = (s || '').split('@');
  return domain ? domain : '';
};

export interface DispatchProps {
  onClose: () => void;
  readonly submitTeamCreation: (args: {
    type: TeamType | null;
    displayName: string;
    teamType?: string;
    desc?: string;
  }) => Promise<OrganizationsResponse | void>;
  readonly onCreateTeamSuccess: (
    type: TeamType | null,
    team: OrganizationsResponse | {},
  ) => void;
}

export interface StateProps {
  emailAddress?: string | null;
  freeTeamLimitWarning: boolean;
  freeTeamLimitRestriction: boolean;
  isDesktop?: boolean;
  teamType: TeamType | null;
}

interface CreateTeamProps extends DispatchProps, StateProps {}

export const CreateTeamUnconnected = ({
  isDesktop,
  freeTeamLimitWarning,
  freeTeamLimitRestriction,
  emailAddress,
  onClose,
  onCreateTeamSuccess,
  submitTeamCreation,
  teamType,
}: CreateTeamProps) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [team, setTeam] = useState({ id: '', displayName: '', name: '' });
  const [stage, setStage] = useState(1);
  const emailDomain = getEmailDomain(emailAddress);

  useEffect(() => {
    if (!freeTeamLimitRestriction) {
      Analytics.sendScreenEvent({
        name: 'createFreeTeamModal',
        attributes: {
          freeTeamLimitRestriction: freeTeamLimitRestriction,
          freeTeamLimitWarning: freeTeamLimitWarning,
          domainName: emailDomain,
        },
      });
    } else if (emailDomain) {
      Analytics.sendScreenEvent({
        name: 'freeTeamRestrictionModal',
        attributes: {
          domainName: emailDomain,
        },
      });
    }

    if (!freeTeamLimitRestriction && freeTeamLimitWarning && emailDomain) {
      trackUe({
        category: Noun.TEAM_BUILDER,
        verb: Verb.SHOWS,
        directObj: Noun.FREE_TEAM_LIMIT_WARNING_MESSAGE,
        context: {
          domainName: emailDomain,
          env: isDesktop ? 'desktop' : 'web',
        },
      });
    }
    if (freeTeamLimitRestriction && emailDomain) {
      trackUe({
        category: Noun.TEAM_BUILDER,
        verb: Verb.SHOWS,
        directObj: Noun.FREE_TEAM_LIMIT_RESTRICTION_MESSAGE_TEAM_BUILDER,
        context: {
          domainName: emailDomain,
          env: isDesktop ? 'desktop' : 'web',
        },
      });
    }
  }, [freeTeamLimitWarning, freeTeamLimitRestriction, emailDomain, isDesktop]);

  useEffect(() => {
    if (stage > 1 && team.name) {
      // sets the home button to go back to team home rather than old teams
      TrelloStorage.set(`home_${memberId}_last_tab_2`, `/${team.name}/home`);
    }
  }, [stage, team.name]);

  function startAnimation() {
    setAnimationStep(animationStep + 1);
  }

  function submitCreate({
    displayName,
    teamVerticalType,
    desc,
  }: {
    displayName: string;
    teamVerticalType?: string;
    desc?: string;
  }) {
    submitTeamCreation({
      type: teamType,
      displayName,
      teamType: teamVerticalType,
      desc,
    }).then((organizationsResponse: void | OrganizationsResponse) => {
      if (organizationsResponse) {
        const normalizedTeam = normalizeTeam(organizationsResponse);
        setTeam(normalizedTeam || {});
        setStage(2);
      } else {
        // TODO error handling for failed team creation
      }
    });
  }

  function onEnterpriseLinkClick() {
    trackUe({
      category: Noun.TEAM_BUILDER,
      verb: Verb.CLICKS,
      directObj: Noun.FREE_TEAM_LIMIT_WARNING_MESSAGE_ENTERPRISE_LINK,
      context: {
        domainName: emailDomain,
        env: isDesktop ? 'desktop' : 'web',
      },
    });

    Analytics.sendClickedLinkEvent({
      linkName: 'enterpriseLink',
      source: 'createFreeTeamModal',
      attributes: {
        domainName: emailDomain,
      },
    });
  }

  function onEnterpriseButtonClick() {
    trackUe({
      category: Noun.TEAM_BUILDER,
      verb: Verb.CLICKS,
      directObj: Noun.FREE_TEAM_LIMIT_RESTRICTION_MESSAGE_ENTERPRISE_BUTTON,
      context: {
        domainName: emailDomain,
        env: isDesktop ? 'desktop' : 'web',
      },
    });

    Analytics.sendClickedButtonEvent({
      buttonName: 'enterpriseButton',
      source: 'createFreeTeamModal',
      attributes: {
        domainName: emailDomain,
      },
    });
  }

  const showLaterButton: boolean = stage === 2;
  const { id = '', displayName = '', name = '' } = team;

  const businessClassLinkParams = [
    'utm_source=trello-product',
    'utm_medium=inapp',
    'utm_campaign=P:trello|O:trello|F:awareness|C:ptour|I:trello-business-class-in-product|E:cloud|',
    `app_source=team_builder`,
  ].join('&');

  return (
    <div className={styles.createTeamContainer}>
      <div className={styles.formContainer}>
        {freeTeamLimitWarning && !freeTeamLimitRestriction && stage === 1 && (
          <div className={styles.messageContainer}>
            <div className={styles.freeTeamWarningMessage}>
              <strong>
                {format('your-org-is-running-out-of-teams', {
                  emailDomain: emailDomain ? `(${emailDomain})` : '',
                })}
              </strong>{' '}
              {isDesktop ? (
                <span>{format('contact-your-admin')}</span>
              ) : (
                <span>
                  {format('learn-more-about-trello-enterprise', {
                    trelloEnterprise: (
                      <a
                        href="/enterprise?utm_source=trello-product&utm_medium=inapp&utm_campaign=P:trello|O:trello|F:awareness|C:ptour|I:trello-enterprise-in-product|E:cloud|&app_source=warning_message_team_builder"
                        target="_blank"
                        key="enterprise-link"
                        onClick={onEnterpriseLinkClick}
                      >
                        Trello Enterprise
                      </a>
                    ),
                  })}
                </span>
              )}
            </div>
          </div>
        )}
        {freeTeamLimitRestriction && (
          <div className={styles.restrictionContainer}>
            {isDesktop ? (
              <h3>
                {format('your-organization-has-run-out-of-teams-desktop', {
                  domain: emailDomain,
                })}
              </h3>
            ) : (
              <>
                <h3>
                  {format('your-organization-has-run-out-of-teams', {
                    domain: emailDomain,
                  })}
                </h3>
                <p className={styles.createTeamRestrictedDescription}>
                  {format(
                    'contact-your-administrator-about-upgrading-to-trello-enterprise',
                  )}
                </p>
                <ButtonLink
                  isPrimary={true}
                  link="/enterprise?utm_source=trello-product&utm_medium=inapp&utm_campaign=P:trello|O:trello|F:awareness|C:ptour|I:trello-enterprise-in-product|E:cloud|&app_source=exceeded_message_team_builder"
                  onClick={onEnterpriseButtonClick}
                >
                  {format('learn-more')}
                </ButtonLink>
                <p className={styles.bcUpsell}>
                  {format('bc upsell', {
                    businessClassLink: (
                      <a
                        href={`/business-class?${businessClassLinkParams}`}
                        target="_blank"
                        key="business-class-link"
                        onClick={() => {
                          Analytics.sendUIEvent({
                            action: 'clicked',
                            actionSubject: 'link',
                            actionSubjectId: 'businessClassLink',
                            source: 'createFreeTeamModal',
                            attributes: {
                              domainName: emailDomain,
                            },
                          });
                          trackUe({
                            category: Noun.BANNER,
                            verb: Verb.CLICKS,
                            directObj:
                              Noun.FREE_TEAM_LIMIT_WARNING_MESSAGE_BUSINESS_CLASS_LINK,
                            context: {
                              domainName: emailDomain,
                              env: 'web',
                            },
                          });
                        }}
                      >
                        Business Class
                      </a>
                    ),
                  })}
                </p>
              </>
            )}
          </div>
        )}
        {!freeTeamLimitRestriction && stage === 1 && (
          <CreateTeamPanel
            submitCreateTeam={submitCreate}
            startAnimation={startAnimation}
            animationStep={animationStep}
          />
        )}
        {!freeTeamLimitRestriction && stage === 2 && (
          <InviteTeamMembersForm
            startAnimation={startAnimation}
            orgId={id}
            orgDisplayName={displayName}
            model={null}
            className={styles.inviteFormWrapper}
            fullWidthButton
            preventTextareaResize
            onInviteTeamMembersSuccess={onCreateTeamSuccess.bind(
              this,
              teamType,
              team,
            )}
          />
        )}
        {!freeTeamLimitRestriction && showLaterButton && (
          <RouterLink
            className={styles.doLaterLinkButton}
            testId={TeamTestIds.ShowLaterButton}
            onClick={onClose}
            href={`/${name}${teamType === TeamType.Business ? '/billing' : ''}`}
          >
            {format('ill-do-this-later')}
          </RouterLink>
        )}
      </div>
      <div className={styles.illustrationContainer}>
        <div className={styles.illustrationWrapper}>
          <TeamIllustrationAnimation animationStep={animationStep} />
        </div>
      </div>
      <CloseButton
        closeIcon={<CloseIcon color="dark" size="large" />}
        className={styles.closeButton}
        onClick={onClose}
      />
    </div>
  );
};
